/**
 * PrimitiveFieldConstellationGraph — Cytoscape.js concentric-ring visualization
 * for FERP primitive field structures.
 *
 * Center: Primitive name (e.g., "Task")
 * Ring 1-4: Category nodes (Identity, Assignment, etc.)
 * Expanded: Individual field nodes (click a category to expand)
 *
 * With context profile active:
 * - Promoted fields glow (yellow border)
 * - Non-promoted fields dim (30% opacity)
 * - Highlighted rings get emphasized borders
 *
 * Pattern follows RegulatoryConstellationGraph.tsx / UniversalConstellationGraph.tsx.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Group, Button, Text, Badge, Paper, Stack } from '@mantine/core';

import type {
  PrimitiveConstellationData,
  FieldConstellationNode,
  FieldConstellationEdge,
  ConstellationRingMeta,
} from './hooks/usePrimitiveConstellation';
import { RING_CONCENTRIC_VALUES } from './hooks/usePrimitiveConstellation';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PrimitiveFieldConstellationGraphProps {
  /** Constellation data from usePrimitiveConstellation. */
  constellation: PrimitiveConstellationData | null;
  /** Container height in pixels. */
  height?: number;
  /** Callback when a field node is selected. */
  onFieldSelect?: (fieldName: string | null) => void;
  /** Callback when a category is double-clicked (expand/collapse). */
  onCategoryToggle?: (ringId: string) => void;
  /** Set of expanded category ring IDs. */
  expandedCategories?: Set<string>;
  /** Whether a context profile is active (enables dimming). */
  contextActive?: boolean;
}

// ---------------------------------------------------------------------------
// Color constants
// ---------------------------------------------------------------------------

const CENTER_COLOR = '#7048e8';
const PROMOTED_BORDER_COLOR = '#ffd43b';
const DIMMED_OPACITY = 0.3;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PrimitiveFieldConstellationGraph({
  constellation,
  height = 600,
  onFieldSelect,
  onCategoryToggle,
  expandedCategories = new Set(),
  contextActive = false,
}: PrimitiveFieldConstellationGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // Initialize Cytoscape (once)
  useEffect(() => {
    let mounted = true;

    async function initCytoscape() {
      if (!containerRef.current || cyRef.current) return;

      // Dynamic import to avoid SSR issues
      const cytoscapeModule = await import('cytoscape');
      const cytoscape = cytoscapeModule.default;

      if (!mounted || !containerRef.current) return;

      cyRef.current = cytoscape({
        container: containerRef.current,
        style: CONSTELLATION_STYLES,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 1,
      });

      // Event handlers
      cyRef.current.on('tap', 'node', (event: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const node = event.target;
        const role = node.data('role');
        const nodeId = node.id();

        if (role === 'center') {
          setSelectedField(null);
          onFieldSelect?.(null);
        } else if (role === 'category') {
          const ringId = node.data('ring_id');
          onCategoryToggle?.(ringId);
        } else if (role === 'field') {
          const fieldName = node.data('field_name');
          setSelectedField(fieldName);
          onFieldSelect?.(fieldName);
        }
      });

      cyRef.current.on('tap', (event: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (event.target === cyRef.current) {
          setSelectedField(null);
          onFieldSelect?.(null);
        }
      });

      setIsInitialized(true);
    }

    initCytoscape();

    return () => {
      mounted = false;
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update graph when constellation data changes
  useEffect(() => {
    if (!cyRef.current || !isInitialized || !constellation) return;

    const cy = cyRef.current;
    cy.elements().remove();

    const cyNodes: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
    const cyEdges: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

    // Center node
    cyNodes.push({
      data: {
        id: constellation.center.nodeId,
        label: constellation.center.name,
        role: 'center',
        concentric_value: RING_CONCENTRIC_VALUES[0],
      },
      style: {
        'background-color': CENTER_COLOR,
        'border-color': '#5f3dc4',
        width: 120,
        height: 75,
        shape: 'round-rectangle',
        'font-size': 14,
        'font-weight': 'bold',
        color: '#fff',
      },
    });

    // Category nodes
    for (const cat of constellation.categoryNodes) {
      const isExpanded = expandedCategories.has(cat.ringId ?? '');
      const label = `${cat.name}\n(${cat.fieldCount ?? 0})`;

      cyNodes.push({
        data: {
          id: cat.nodeId,
          label,
          role: 'category',
          ring_id: cat.ringId,
          concentric_value: RING_CONCENTRIC_VALUES[cat.ringNumber] ?? 40,
          is_promoted: cat.promoted,
          is_expanded: isExpanded,
        },
        style: {
          'background-color': cat.color ?? '#228be6',
          'border-color': cat.promoted ? PROMOTED_BORDER_COLOR : (cat.color ?? '#228be6'),
          'border-width': cat.promoted ? 4 : 2,
          width: 90,
          height: 55,
          shape: cat.shape ?? 'round-rectangle',
          'font-size': 10,
          'font-weight': 'bold',
          color: '#fff',
          'text-wrap': 'wrap',
          'text-max-width': '85px',
          opacity: contextActive && !cat.promoted ? DIMMED_OPACITY : 1,
        },
      });

      // Edge: center → category
      cyEdges.push({
        data: {
          id: `edge-${constellation.center.nodeId}-${cat.nodeId}`,
          source: constellation.center.nodeId,
          target: cat.nodeId,
          layer: 'category',
        },
        style: {
          width: 3,
          'line-color': cat.color ?? '#228be6',
          'target-arrow-color': cat.color ?? '#228be6',
          opacity: contextActive && !cat.promoted ? DIMMED_OPACITY : 0.7,
        },
      });
    }

    // Field nodes (only for expanded categories)
    for (const field of constellation.fieldNodes) {
      if (!expandedCategories.has(field.ringId ?? '')) continue;

      const isDimmed = contextActive && !field.promoted;

      cyNodes.push({
        data: {
          id: field.nodeId,
          label: field.name,
          role: 'field',
          field_name: field.nodeId.replace('field-', ''),
          ring_id: field.ringId,
          concentric_value: (RING_CONCENTRIC_VALUES[field.ringNumber] ?? 40) - 10,
          is_promoted: field.promoted,
          field_type: field.fieldType,
          required: field.required,
        },
        style: {
          'background-color': field.color ?? '#228be6',
          'border-color': field.promoted ? PROMOTED_BORDER_COLOR : (field.color ?? '#228be6'),
          'border-width': field.promoted ? 4 : 1,
          width: 65,
          height: 40,
          shape: 'round-rectangle',
          'font-size': 8,
          color: '#fff',
          'text-wrap': 'wrap',
          'text-max-width': '60px',
          opacity: isDimmed ? DIMMED_OPACITY : 1,
          ...(field.promoted
            ? {
                'shadow-blur': 10,
                'shadow-color': PROMOTED_BORDER_COLOR,
                'shadow-opacity': 0.7,
              }
            : {}),
        },
      });
    }

    // Field edges (only for expanded categories)
    for (const edge of constellation.edges) {
      if (edge.layer !== 'field') continue;
      const fieldNode = constellation.fieldNodes.find(
        (f) => f.nodeId === edge.targetId,
      );
      if (!fieldNode || !expandedCategories.has(fieldNode.ringId ?? '')) continue;

      cyEdges.push({
        data: {
          id: `edge-${edge.sourceId}-${edge.targetId}`,
          source: edge.sourceId,
          target: edge.targetId,
          layer: 'field',
        },
        style: {
          width: 1.5,
          'line-color': fieldNode.color ?? '#adb5bd',
          'target-arrow-color': fieldNode.color ?? '#adb5bd',
          opacity: contextActive && !fieldNode.promoted ? DIMMED_OPACITY : 0.5,
        },
      });
    }

    // Add elements and run layout
    cy.add(cyNodes);
    cy.add(cyEdges);

    cy.layout({
      name: 'concentric',
      concentric: (node: any) => node.data('concentric_value') ?? 15, // eslint-disable-line @typescript-eslint/no-explicit-any
      levelWidth: () => 1,
      minNodeSpacing: 30,
      animate: true,
      animationDuration: 600,
      padding: 30,
    }).run();
  }, [constellation, isInitialized, expandedCategories, contextActive, onFieldSelect, onCategoryToggle]);

  // Fit to view handler
  const handleFitToView = useCallback(() => {
    cyRef.current?.fit(undefined, 40);
  }, []);

  if (!constellation) {
    return (
      <Box
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text c="dimmed">Select a primitive to view its field constellation.</Text>
      </Box>
    );
  }

  return (
    <Box pos="relative">
      {/* Stats bar */}
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Badge variant="filled" color="violet">
            {constellation.totalFieldCount} fields
          </Badge>
          {constellation?.activeContextId && (
            <Badge variant="filled" color="yellow" c="dark">
              {constellation.promotedFieldCount} promoted
            </Badge>
          )}
          <Badge variant="outline" color="gray">
            {constellation.rings.length} categories
          </Badge>
        </Group>
        <Button size="xs" variant="subtle" onClick={handleFitToView}>
          Fit to view
        </Button>
      </Group>

      {/* Cytoscape container */}
      <Box
        ref={containerRef}
        style={{
          height,
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-md)',
          background: 'var(--mantine-color-gray-0)',
        }}
      />

      {/* Legend */}
      <Paper p="xs" mt="xs" withBorder>
        <Group gap="md">
          <Group gap={4}>
            <Box
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: CENTER_COLOR,
              }}
            />
            <Text size="xs">Primitive</Text>
          </Group>
          {constellation?.rings.map((ring) => (
            <Group gap={4} key={ring.ringId}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: ring.shape === 'ellipse' ? '50%' : 2,
                  background: ring.color,
                }}
              />
              <Text size="xs">
                {ring.label} ({ring.fieldCount})
              </Text>
            </Group>
          ))}
          {constellation?.activeContextId && (
            <Group gap={4}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  border: `2px solid ${PROMOTED_BORDER_COLOR}`,
                  background: 'transparent',
                }}
              />
              <Text size="xs">Promoted</Text>
            </Group>
          )}
        </Group>
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Cytoscape stylesheet
// ---------------------------------------------------------------------------

const CONSTELLATION_STYLES: any[] = [ // eslint-disable-line @typescript-eslint/no-explicit-any
  {
    selector: 'node',
    style: {
      'background-color': '#228be6',
      'border-width': 2,
      'border-color': '#1864ab',
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 10,
      'font-weight': 'bold',
      color: '#fff',
      width: 65,
      height: 50,
      'text-wrap': 'wrap',
      'text-max-width': '80px',
    },
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 5,
      'border-color': '#e03131',
      'overlay-color': '#e03131',
      'overlay-opacity': 0.15,
    },
  },
  {
    selector: 'edge',
    style: {
      width: 2,
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      opacity: 0.6,
    },
  },
];
